import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import { NotificationsProvider } from "@/lib/notifications";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";

import HomePage from "@/pages/HomePage";
import ProductsPage from "@/pages/ProductsPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import QuotePage from "@/pages/QuotePage";
import ContactPage from "@/pages/ContactPage";
import AboutPage from "@/pages/AboutPage";
import BlogPostPage from "@/pages/BlogPostPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";

import AdminLayout from "@/pages/AdminLayout";
import DashboardTab from "@/pages/admin/DashboardTab";
import QuotesTab from "@/pages/admin/QuotesTab";
import JobsTab from "@/pages/admin/JobsTab";
import BookingsCalendarTab from "@/pages/admin/BookingsCalendarTab";
import InvoicesTab from "@/pages/admin/InvoicesTab";
import AccountingTab from "@/pages/admin/AccountingTab";
import CustomersTab from "@/pages/admin/CustomersTab";
import ProductsTab from "@/pages/admin/ProductsTab";
import GalleryTab from "@/pages/admin/GalleryTab";
import BlogsTab from "@/pages/admin/BlogsTab";
import ChatTab from "@/pages/admin/ChatTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import InvoicePrintPage from "@/pages/InvoicePrintPage";

function ProtectedCustomer() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <Outlet />;
}

function ProtectedAdmin() {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return <Outlet />;
}

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <ChatWidget />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SettingsProvider>
        <AuthProvider>
          <NotificationsProvider>
            <BrowserRouter>
              <Routes>
                <Route element={<div className="min-h-screen"><Header /><Outlet /></div>}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/admin/login" element={<LoginPage />} />
                </Route>

                <Route element={<ProtectedAdmin />}>
                  <Route element={<AdminLayout />}>
                    <Route path="/admin" element={<DashboardTab />} />
                    <Route path="/admin/quotes" element={<QuotesTab />} />
                    <Route path="/admin/jobs" element={<JobsTab />} />
                    <Route path="/admin/bookings" element={<BookingsCalendarTab />} />
                    <Route path="/admin/invoices" element={<InvoicesTab />} />
                    <Route path="/admin/accounting" element={<AccountingTab />} />
                    <Route path="/admin/customers" element={<CustomersTab />} />
                    <Route path="/admin/products" element={<ProductsTab />} />
                    <Route path="/admin/gallery" element={<GalleryTab />} />
                    <Route path="/admin/blogs" element={<BlogsTab />} />
                    <Route path="/admin/chat" element={<ChatTab />} />
                    <Route path="/admin/settings" element={<SettingsTab />} />
                  </Route>
                  <Route path="/invoice/:id" element={<InvoicePrintPage />} />
                </Route>

                <Route element={<PublicLayout />}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/quote" element={<QuotePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route element={<ProtectedCustomer />}>
                    <Route path="/dashboard" element={<CustomerDashboard />} />
                  </Route>
                </Route>
              </Routes>
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </NotificationsProvider>
        </AuthProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;